// import { useEffect, useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import { registerUser, clearAuthError } from "../slice/authSlice";
// import AuthLayout, { PasswordInput, SubmitButton, AuthAlert } from "../components/AuthLayout";

// const ROLES = [
//   { value: "RECRUITER", label: "Recruiter" },
//   { value: "TEAM_LEAD", label: "Team Lead" },
// ];

// export default function Register() {
//   const [name, setName] = useState("");
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [contact, setContact] = useState("");
//   const [role, setRole] = useState("RECRUITER");

//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { status, error } = useSelector((state) => state.auth);
//   const loading = status === "loading";

//   // Clear any old error (e.g. from the Login page) when this page opens.
//   useEffect(() => {
//     dispatch(clearAuthError());
//   }, [dispatch]);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     dispatch(clearAuthError());
//     const result = await dispatch(registerUser({ name, email, password, role, contact }));

//     if (registerUser.fulfilled.match(result)) {
//       navigate("/dashboard");
//     }
//   };

//   const passwordTooShort = password.length > 0 && password.length < 6;

//   return (
//     <AuthLayout
//       title="Create your account"
//       // subtitle="Set up your LoneWolf login to start optimizing candidate resumes."
//       footer={
//         <>
//           Already have an account? <Link to="/login">Sign in</Link>
//         </>
//       }
//     >
//       {error && <AuthAlert tone="error">{error}</AuthAlert>}

//       <form className="au-form" onSubmit={handleSubmit}>
//         <div className="au-field">
//           <label className="au-label" htmlFor="reg-name">Full name</label>
//           <input
//             id="reg-name"
//             className="au-input"
//             type="text"
//             value={name}
//             onChange={(e) => setName(e.target.value)}
//             placeholder="John Doe"
//             autoComplete="name"
//             required
//           />
//         </div>

//         <div className="au-field">
//           <label className="au-label" htmlFor="reg-email">Work email</label>
//           <input
//             id="reg-email"
//             className="au-input"
//             type="email"
//             value={email}
//             onChange={(e) => setEmail(e.target.value)}
//             placeholder="you@company.com"
//             autoComplete="email"
//             required
//           />
//         </div>

//         <div className="au-field">
//           <label className="au-label" htmlFor="reg-password">Password</label>
//           <PasswordInput
//             id="reg-password"
//             value={password}
//             onChange={(e) => setPassword(e.target.value)}
//             placeholder="At least 6 characters"
//             autoComplete="new-password"
//           />
//           <p className={`au-hint${passwordTooShort ? " au-hint-error" : ""}`}>
//             Use at least 6 characters.
//           </p>
//         </div>

//         <div className="au-field">
//           <label className="au-label" htmlFor="reg-contact">
//             Contact number <span className="au-optional">(optional)</span>
//           </label>
//           <input
//             id="reg-contact"
//             className="au-input"
//             type="tel"
//             value={contact}
//             onChange={(e) => setContact(e.target.value)}
//             placeholder="e.g. 9876543210"
//             autoComplete="tel"
//           />
//         </div>

//         <div className="au-field">
//           <span className="au-label" id="reg-role-label">Role</span>
//           <div className="au-roles" role="radiogroup" aria-labelledby="reg-role-label">
//             {ROLES.map((r) => (
//               <label
//                 key={r.value}
//                 className={`au-role${role === r.value ? " is-selected" : ""}`}
//               >
//                 <input
//                   type="radio"
//                   name="role"
//                   value={r.value}
//                   checked={role === r.value}
//                   onChange={(e) => setRole(e.target.value)}
//                 />
//                 <span className="au-role-dot" aria-hidden="true" />
//                 {r.label}
//               </label>
//             ))}
//           </div>
//         </div>

//         <SubmitButton loading={loading} loadingText="Creating account...">
//           Create account
//         </SubmitButton>
//       </form>
//     </AuthLayout>
//   );
// }

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, clearAuthError } from "../slice/authSlice";
import AuthLayout, { PasswordInput, SubmitButton, AuthAlert } from "../components/AuthLayout";
import ResendVerification from "../components/ResendVerification";

const ROLES = [
  { value: "RECRUITER", label: "Recruiter" },
  { value: "TEAM_LEAD", label: "Team Lead" },
];

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [contact, setContact] = useState("");
  const [role, setRole] = useState("RECRUITER");

  // Set after a successful sign-up: the address the verification link went to.
  const [sentTo, setSentTo] = useState("");

  const dispatch = useDispatch();
  const { status, error, needsVerification } = useSelector((state) => state.auth);
  const loading = status === "loading";

  // Clear any old error (e.g. from the Login page) when this page opens.
  useEffect(() => {
    dispatch(clearAuthError());
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearAuthError());
    const result = await dispatch(registerUser({ name, email, password, role, contact }));

    // Account created but not active yet: show the "check your email" screen
    // instead of going to the dashboard.
    if (registerUser.fulfilled.match(result)) {
      setSentTo(result.payload.email || email.trim().toLowerCase());
    }
  };

  const passwordTooShort = password.length > 0 && password.length < 6;

  if (sentTo) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={`We've sent a verification link to ${sentTo}. Click it to activate your account, then sign in.`}
        footer={
          <>
            Already verified? <Link to="/login">Sign in</Link>
          </>
        }
      >
        <AuthAlert tone="success">
          The link expires in 24 hours. If you don't see the email, check your spam folder.
        </AuthAlert>
        <ResendVerification email={sentTo} label="Didn't get it? Resend the link" />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      // subtitle="Set up your LoneWolf login to start optimizing candidate resumes."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      {error && <AuthAlert tone="error">{error}</AuthAlert>}

      {/* Registered before but never clicked the link */}
      {needsVerification && <ResendVerification email={email} />}

      <form className="au-form" onSubmit={handleSubmit}>
        <div className="au-field">
          <label className="au-label" htmlFor="reg-name">Full name</label>
          <input
            id="reg-name"
            className="au-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            autoComplete="name"
            required
          />
        </div>

        <div className="au-field">
          <label className="au-label" htmlFor="reg-email">Work email</label>
          <input
            id="reg-email"
            className="au-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            required
          />
        </div>

        <div className="au-field">
          <label className="au-label" htmlFor="reg-password">Password</label>
          <PasswordInput
            id="reg-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            autoComplete="new-password"
          />
          <p className={`au-hint${passwordTooShort ? " au-hint-error" : ""}`}>
            Use at least 6 characters.
          </p>
        </div>

        <div className="au-field">
          <label className="au-label" htmlFor="reg-contact">
            Contact number <span className="au-optional">(optional)</span>
          </label>
          <input
            id="reg-contact"
            className="au-input"
            type="tel"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="e.g. 9876543210"
            autoComplete="tel"
          />
        </div>

        <div className="au-field">
          <span className="au-label" id="reg-role-label">Role</span>
          <div className="au-roles" role="radiogroup" aria-labelledby="reg-role-label">
            {ROLES.map((r) => (
              <label
                key={r.value}
                className={`au-role${role === r.value ? " is-selected" : ""}`}
              >
                <input
                  type="radio"
                  name="role"
                  value={r.value}
                  checked={role === r.value}
                  onChange={(e) => setRole(e.target.value)}
                />
                <span className="au-role-dot" aria-hidden="true" />
                {r.label}
              </label>
            ))}
          </div>
        </div>

        <SubmitButton loading={loading} loadingText="Creating account...">
          Create account
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}